import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import classNames from "classnames";
import { Dialog } from "components/dialog";
import Button from "components/form/button";
import { DATATEST } from "constants/integration";

interface IProps {
    modalOpen: boolean;
    exportHandler: () => void;
    retryHandler: () => void;
    clearHandler: () => void;
}

export const PendingMutationModal: React.FunctionComponent<IProps> = ({
    modalOpen,
    retryHandler,
    clearHandler,
    exportHandler,
}) => {
    const muiStyles = {
        button: {
            marginLeft: "0.25em",
            marginRight: "0.25em",
        },
        dialog: {
            zIndex: "9999",
            paddingTop: "16px",
        },
        customContentStyle: {
            maxWidth: "640px",
        },
    };

    const [isClearConfirmation, setIsClearConfirmation] = React.useState(false);

    const getDialogTitle = () => {
        if (isClearConfirmation) {
            return formatMessage("Clear Unsaved Changes");
        }
        return formatMessage("Your assortment has unsaved changes.");
    };

    const getDialogContent = () => {
        if (isClearConfirmation) {
            return (
                <React.Fragment>
                    <p>{formatMessage("This will delete any unsaved changes in your assortment. Are you sure?")}</p>
                    <p>{formatMessage("We suggest exporting to XLS first to keep a record of your notes.")}</p>
                </React.Fragment>
            );
        }
        return (
            <p>
                {formatMessage(
                    "Due to a connectivity issue, we have been unable to save your most recent edits. How would you like to proceed?"
                )}
            </p>
        );
    };

    const handleOnClear = () => {
        if (!isClearConfirmation) {
            setIsClearConfirmation(true);
            return;
        }

        clearHandler();
    };

    const handleClearConfirmationBack = () => {
        setIsClearConfirmation(false);
    };

    const getDialogActions = () => {
        return [
            isClearConfirmation && (
                <Button
                    key="cancel-clear"
                    data-test={DATATEST.ASSORTMENTS.PENDING_COLLECTION_PURGE_CANCEL}
                    label={formatMessage("Back")}
                    onClick={handleClearConfirmationBack}
                    style={muiStyles.button}
                />
            ),
            <Button
                key="export-xls"
                data-test={DATATEST.ASSORTMENTS.PENDING_COLLECTION_EXPORT}
                label={formatMessage("Export Assortment to XLS")}
                onClick={exportHandler}
                style={muiStyles.button}
            />,
            <Button
                key="clear"
                data-test={DATATEST.ASSORTMENTS.PENDING_COLLECTION_PURGE}
                label={formatMessage("Clear Unsaved Changes")}
                onClick={handleOnClear}
                style={muiStyles.button}
            />,
            !isClearConfirmation && (
                <Button
                    primary={true}
                    key="retry"
                    data-test={DATATEST.ASSORTMENTS.PENDING_COLLECTION_RETRY}
                    label={formatMessage("Retry")}
                    onClick={retryHandler}
                    style={muiStyles.button}
                />
            ),
        ];
    };

    return (
        <Dialog
            open={modalOpen}
            contentStyle={muiStyles.customContentStyle}
            modal={true}
            showCloseButton={false}
            style={muiStyles.dialog}
            title={getDialogTitle()}
            actions={getDialogActions()}
        >
            <div
                className={classNames({
                    "dialog-content": true,
                    "dialog-content--clear-confirmation": isClearConfirmation,
                })}
            >
                {getDialogContent()}
            </div>
        </Dialog>
    );
};
